/**
 * What a reload opens (2026-11-03, operator: "can we offer the user the option to choose reload being same as you left
 * or onto canvas?", "being the same as you left, being the default"). Settings > "After a reload": WHERE YOU LEFT OFF
 * (default) or THE CANVAS.
 *
 * "Where you left off" is the screen: the timeline editor on its timeline, or Navigate on its section (Help opens at
 * its home). Kept per TAB (sessionStorage): a reload of this tab reopens it, a new tab or the iPad app started fresh
 * opens on the canvas as always. The choice is kept per device (localStorage). Nothing here touches what a project or a
 * timeline holds, only which screen is up.
 */

import { createEffect, createRoot, createSignal } from 'solid-js';
import {
  openLegacyTimelineEditor, openTimelineEditor, getTimeline, timelineEditorKind, timelineEditorOpen, timelineEditorTargetId,
} from '../timeline/store.ts';
import { navigateOpen, navigatePane, openNavigateAt, type NavPane } from './store.ts';
import { showPane } from './panes.ts';

export type ReloadOpens = 'left' | 'canvas';
const PREF_KEY = 'opbuttons.reloadOpens.v1';
const SCREEN_KEY = 'opbuttons.screen.v1';

const readPref = (): ReloadOpens => { try { return localStorage.getItem(PREF_KEY) === 'canvas' ? 'canvas' : 'left'; } catch { return 'left'; } };
const [pref, setPrefSig] = createSignal<ReloadOpens>(readPref());
export const reloadOpens = pref;
export function setReloadOpens(v: ReloadOpens): void {
  setPrefSig(v);
  try { if (v === 'left') localStorage.removeItem(PREF_KEY); else localStorage.setItem(PREF_KEY, v); } catch { /* a convenience */ }
}

interface Screen { editor?: { id: string | null; legacy: boolean }; pane?: NavPane }

function current(): Screen {
  if (timelineEditorOpen()) return { editor: { id: timelineEditorTargetId(), legacy: timelineEditorKind() === 'legacy' } };
  if (navigateOpen()) return { pane: navigatePane() };
  return {};
}

let installed = false;

/** Reopen what this tab had open (when the setting says so), then keep the record current. Call once, after the app
 *  has its project (App's mount). */
export function installResume(hasProject: boolean): void {
  if (installed) return;
  installed = true;
  let saved: Screen = {};
  try { saved = JSON.parse(sessionStorage.getItem(SCREEN_KEY) ?? '{}') as Screen; } catch { /* nothing to reopen */ }
  if (hasProject && pref() === 'left') {
    if (saved.editor) {
      // A timeline deleted since (on another device) is not reopened.
      if (!saved.editor.id || getTimeline(saved.editor.id)) (saved.editor.legacy ? openLegacyTimelineEditor : openTimelineEditor)(saved.editor.id);
    } else if (saved.pane) {
      showPane(saved.pane);
      if (!navigateOpen()) openNavigateAt(saved.pane);
    }
  }
  createRoot(() => {
    createEffect(() => {
      const s = current();
      try { sessionStorage.setItem(SCREEN_KEY, JSON.stringify(s)); } catch { /* a convenience */ }
    });
  });
}
